import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, Image as ImageIcon, FileText, RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { getMediaUrl } from '../api/client.js';

export interface ImageDropzoneProps {
  value: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  maxSizeBytes?: number; // default 5 MB
  label?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  initialPreviewUrl?: string | null;
}

export function ImageDropzone({
  value,
  onChange,
  accept = 'image/jpeg,image/png,image/webp,application/pdf',
  maxSizeBytes = 5 * 1024 * 1024, // 5MB
  label,
  hint = 'PNG, JPG, WebP or PDF (up to 5MB)',
  className = '',
  disabled = false,
  initialPreviewUrl,
}: ImageDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(getMediaUrl(initialPreviewUrl) || null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Manage object URL lifecycle
  useEffect(() => {
    if (!value) {
      setPreviewUrl(getMediaUrl(initialPreviewUrl) || null);
      return;
    }

    if (value.type.startsWith('image/')) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [value, initialPreviewUrl]);

  const validateAndSetFile = (file: File) => {
    setErrorMessage(null);

    // Size validation
    if (file.size > maxSizeBytes) {
      const maxMb = Math.round(maxSizeBytes / (1024 * 1024));
      setErrorMessage(`File is too large. Maximum size is ${maxMb}MB.`);
      return;
    }

    // MIME type validation if accept is provided
    if (accept && accept !== '*/*') {
      const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());
      const fileType = file.type.toLowerCase();
      const isAccepted = acceptedTypes.some((type) => {
        if (type.endsWith('/*')) {
          const prefix = type.slice(0, -2);
          return fileType.startsWith(prefix);
        }
        return fileType === type;
      });

      if (!isAccepted) {
        setErrorMessage('File type not supported. Please select an image or PDF.');
        return;
      }
    }

    onChange(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
    // Reset input value so the same file can be re-selected if desired
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleTriggerPicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setErrorMessage(null);
    onChange(null);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isPdf = value?.type === 'application/pdf';
  const hasContent = Boolean(value || previewUrl);

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-xs font-medium text-foreground-secondary">
          {label}
        </label>
      )}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled}
        className="hidden"
      />

      {/* Dropzone Container */}
      {!hasContent ? (
        // Empty State: Clickable Dropzone
        <div
          onClick={handleTriggerPicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`group relative flex flex-col items-center justify-center p-5 rounded-[8px] border-2 border-dashed transition-all cursor-pointer text-center ${
            isDragging
              ? 'border-brand-base bg-brand-base/10 scale-[0.99]'
              : 'border-border-secondary hover:border-brand-base/70 bg-background-primary/60 hover:bg-background-secondary/80'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="w-10 h-10 rounded-full bg-brand-10 text-brand-base flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
            <UploadCloud className="w-5 h-5" />
          </div>
          <p className="text-xs font-medium text-foreground-primary">
            Click to upload <span className="text-foreground-tertiary">or drag and drop</span>
          </p>
          {hint && (
            <p className="text-[11px] text-foreground-tertiary mt-1">
              {hint}
            </p>
          )}
        </div>
      ) : (
        // Uploaded State: Preview & Action Toolbar
        <div className="rounded-[8px] border border-border-secondary bg-background-secondary/60 p-3 space-y-3">
          {/* Preview Element */}
          <div className="relative rounded-[6px] overflow-hidden bg-background-primary border border-border-line flex items-center justify-center min-h-[140px] max-h-[220px]">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="File preview"
                className="w-full h-full max-h-[210px] object-contain rounded-[5px]"
              />
            ) : isPdf ? (
              <div className="flex flex-col items-center justify-center p-6 text-center space-y-2">
                <div className="w-12 h-12 rounded-[6px] bg-red-500/10 text-red-500 flex items-center justify-center">
                  <FileText className="w-6 h-6" />
                </div>
                <span className="text-xs font-semibold text-foreground-primary">
                  PDF Document
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-6 text-center text-foreground-tertiary">
                <ImageIcon className="w-8 h-8 mb-1" />
                <span className="text-xs">Image attached</span>
              </div>
            )}
          </div>

          {/* File Meta Info */}
          {value && (
            <div className="flex items-center justify-between text-xs px-1">
              <span className="truncate max-w-[200px] font-medium text-foreground-secondary">
                {value.name}
              </span>
              <span className="font-mono text-[11px] text-foreground-tertiary shrink-0 ml-2">
                {formatFileSize(value.size)}
              </span>
            </div>
          )}

          {/* Action Toolbar: Replace & Remove */}
          <div className="flex items-center gap-2 pt-1 border-t border-border-line/60">
            <button
              type="button"
              onClick={handleTriggerPicker}
              disabled={disabled}
              className="flex-1 py-1.5 px-3 bg-brand-10 hover:bg-brand-20 text-brand-base rounded-[4px] text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Replace Image</span>
            </button>

            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="py-1.5 px-3 border border-border-secondary hover:border-error/40 hover:bg-error/10 text-foreground-secondary hover:text-error rounded-[4px] text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
              title="Remove file"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Remove</span>
            </button>
          </div>
        </div>
      )}

      {/* Error message if validation failed */}
      {errorMessage && (
        <div className="flex items-center gap-1.5 text-xs text-error mt-1 px-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}
